import type { ReactNode } from 'react'
import { Card } from '../Card'
import { DescriptionItem, DescriptionList } from '../DescriptionList'
import { cn } from '../cn'
import { recipeDataAttrs } from './markers'

export type DetailField = {
  /** Stable field identity; used as the React list key. Never index-derived. */
  id: string
  label: ReactNode
  value?: ReactNode
  mono?: boolean
}

export type DetailSurfaceProps = {
  title?: ReactNode
  /** Heading level for the title. Default h2. Use 1 for page-primary titles (walk contracts). */
  titleAs?: 1 | 2 | 3
  /** Optional harvest-stable name for the title heading (data-exact-set-name). */
  titleExactName?: string
  fields?: DetailField[]
  children?: ReactNode
  actions?: ReactNode
  className?: string
  layout?: 'stack' | 'grid' | 'row'
}

/**
 * Detail-1 — read-only detail composition over DescriptionList + Card.
 */
export function DetailSurface({
  title,
  titleAs = 2,
  titleExactName,
  fields = [],
  children,
  actions,
  className = '',
  layout = 'stack',
}: DetailSurfaceProps) {
  const TitleTag = (`h${titleAs}` as 'h1' | 'h2' | 'h3')
  return (
    <div {...recipeDataAttrs('Detail-1')} className={cn('w-full', className)} data-vjal-detail="">
      <Card className="p-6">
        {(title || actions) && (
          <div className="mb-4 flex min-w-0 flex-col items-stretch gap-3 sm:flex-row sm:items-start sm:justify-between">
            {title ? (
              <TitleTag
                className="m-0 min-w-0 font-display text-h3 text-strong"
                data-exact-set-name={titleExactName}
              >
                {title}
              </TitleTag>
            ) : (
              <span />
            )}
            {actions ? <div className="min-w-0 shrink-0 sm:max-w-[50%] sm:self-start">{actions}</div> : null}
          </div>
        )}
        {fields.length > 0 ? (
          <DescriptionList layout={layout}>
            {fields.map((field) => (
              <DescriptionItem key={field.id} label={field.label} mono={field.mono}>
                {field.value}
              </DescriptionItem>
            ))}
          </DescriptionList>
        ) : null}
        {children}
      </Card>
    </div>
  )
}
